import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Edit, Upload, Users, Copy, Download, Trophy } from "lucide-react";

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ employee_id: "", name: "", email: "", department: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // user object being edited, or null
  const [editForm, setEditForm] = useState({ employee_id: "", name: "", email: "", department: "" });
  const [editBusy, setEditBusy] = useState(false);
  const [plUser, setPlUser] = useState(null);
  const [plData, setPlData] = useState(null);

  const openPointsLog = async (u) => {
    setPlUser(u); setPlData(null);
    try { const r = await api.get(`/admin/users/${u.id}/points-log`); setPlData(r.data); }
    catch { toast.error("Failed to load points log"); }
  };

  const load = async () => { const r = await api.get("/admin/users"); setUsers(r.data); };
  useEffect(()=>{ load(); }, []);

  const addOne = async () => {
    if (!form.employee_id || !form.email) return;
    await api.post("/admin/users", form);
    toast.success("User added"); setForm({ employee_id:"", name:"", email:"", department:"" }); load();
  };

  const uploadCsv = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/users/import-csv", fd, { headers:{"Content-Type":"multipart/form-data"}});
      const { added = 0, updated = 0, unchanged = 0 } = r.data || {};
      toast.success(`Sync complete · ${added} added · ${updated} updated · ${unchanged} unchanged`, {
        description: "Existing users kept their tokens. Only new users got fresh tokens.",
      });
      load();
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); setFile(null); }
  };

  const copyLink = (u) => {
    const link = `${window.location.origin}/play?token=${u.token}`;
    navigator.clipboard.writeText(link); toast.success("SSO link copied");
  };

  const remove = async (id) => {
    if (!confirm("Remove user?")) return;
    await api.delete(`/admin/users/${id}`); load();
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      employee_id: u.employee_id || "",
      name: u.name || "",
      email: u.email || "",
      department: u.department || "",
    });
  };

  const closeEdit = () => { setEditing(null); setEditBusy(false); };

  const saveEdit = async () => {
    if (!editing) return;
    if (!editForm.employee_id.trim() || !editForm.email.trim()) {
      toast.error("Employee ID and email are required");
      return;
    }
    setEditBusy(true);
    try {
      await api.patch(`/admin/users/${editing.id}`, editForm);
      toast.success("User updated · token preserved");
      closeEdit();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setEditBusy(false);
    }
  };

  const exportXlsx = async () => {
    try {
      const r = await api.get("/admin/users/export", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `tekwissen_funzone_users_${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Users exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="brutal-card p-5">
          <h3 className="font-black uppercase mb-3 flex items-center gap-2"><Plus size={18}/> Add User Manually</h3>
          <div className="space-y-2">
            <input data-testid="user-empid-input" placeholder="Employee ID" className="brutal-input" value={form.employee_id} onChange={(e)=>setForm({...form, employee_id:e.target.value})}/>
            <input data-testid="user-name-input" placeholder="Name" className="brutal-input" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}/>
            <input data-testid="user-email-input" placeholder="Email" className="brutal-input" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})}/>
            <input data-testid="user-dept-input" placeholder="Department (e.g. Engineering)" className="brutal-input" value={form.department} onChange={(e)=>setForm({...form, department:e.target.value})}/>
            <button data-testid="add-user-btn" onClick={addOne} className="brutal-btn w-full">Add</button>
          </div>
        </div>

        <div className="brutal-card p-5">
          <h3 className="font-black uppercase mb-3 flex items-center gap-2"><Upload size={18}/> Import &amp; Sync (CSV or Excel)</h3>
          <p className="text-xs text-neutral-600 mb-2">
            Columns: <code>employee_id, name, email, department</code> (department optional).
            <br/>
            <strong>Sync rules:</strong> matching <code>employee_id</code> updates name / email / department in-place and <strong>keeps the existing token</strong>. New rows get a fresh token.
          </p>
          <input data-testid="csv-file-input" type="file" accept=".csv,.xlsx,.xlsm" onChange={(e)=>setFile(e.target.files?.[0])} className="block w-full text-sm mb-2"/>
          <button data-testid="upload-csv-btn" onClick={uploadCsv} disabled={!file || busy} className="brutal-btn w-full">{busy?"Syncing…":"Upload & Sync"}</button>
        </div>
      </div>

      <div className="brutal-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h3 className="font-black uppercase">All Users ({users.length})</h3>
          <button
            data-testid="export-users-xlsx-btn"
            onClick={exportXlsx}
            disabled={users.length === 0}
            className="brutal-btn flex items-center gap-2 text-sm disabled:opacity-50"
            title="Download all users (with tokens) as .xlsx"
          >
            <Download size={14}/> Export to Excel
          </button>
        </div>
        <p className="text-xs text-neutral-600 mb-2">Note: the user with employee ID <code className="bg-neutral-200 px-1">DEMO</code> is auto-created. Anyone hitting <code className="bg-neutral-200 px-1">/</code> with no token uses this account — useful for previewing the experience. You can copy its link below.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-black text-white">
              <th className="p-2 text-left">Emp ID</th><th className="p-2 text-left">Name</th><th className="p-2 text-left">Email</th><th className="p-2 text-left">Dept</th><th className="p-2">Actions</th>
            </tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-neutral-200" data-testid={`user-row-${u.employee_id}`}>
                  <td className="p-2 font-mono text-xs">{u.employee_id}</td>
                  <td className="p-2 font-bold">{u.name}</td>
                  <td className="p-2 text-neutral-600">{u.email}</td>
                  <td className="p-2 text-xs">{u.department || <span className="text-neutral-400">—</span>}</td>
                  <td className="p-2 flex gap-1 justify-center">
                    <button data-testid={`copy-link-${u.id}`} onClick={()=>copyLink(u)} title="Copy SSO link" className="p-1 border-2 border-black hover:bg-[#39FF14]"><Copy size={12}/></button>
                    <button data-testid={`points-log-${u.employee_id}`} onClick={()=>openPointsLog(u)} title="View points log" className="p-1 border-2 border-black hover:bg-[#1E88E5] hover:text-white"><Trophy size={12}/></button>
                    <button data-testid={`edit-user-${u.employee_id}`} onClick={()=>openEdit(u)} title="Edit user" className="p-1 border-2 border-black hover:bg-[#FFD600]"><Edit size={12}/></button>
                    <button data-testid={`delete-user-${u.employee_id}`} onClick={()=>remove(u.id)} title="Remove user" className="p-1 border-2 border-black hover:bg-[#FF3B30] hover:text-white"><Trash2 size={12}/></button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-neutral-500">No users yet. Add manually, import CSV, or use the ATS API.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="border-4 border-black rounded-none brutal-shadow max-w-md">
          <DialogHeader>
            <DialogTitle className="uppercase font-black flex items-center gap-2"><Edit size={18}/> Edit User</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs font-black uppercase">Employee ID</label>
            <input data-testid="edit-empid-input" className="brutal-input" value={editForm.employee_id} onChange={(e)=>setEditForm({...editForm, employee_id:e.target.value})}/>
            <label className="text-xs font-black uppercase">Name</label>
            <input data-testid="edit-name-input" className="brutal-input" value={editForm.name} onChange={(e)=>setEditForm({...editForm, name:e.target.value})}/>
            <label className="text-xs font-black uppercase">Email</label>
            <input data-testid="edit-email-input" className="brutal-input" value={editForm.email} onChange={(e)=>setEditForm({...editForm, email:e.target.value})}/>
            <label className="text-xs font-black uppercase">Department</label>
            <input data-testid="edit-dept-input" className="brutal-input" value={editForm.department} onChange={(e)=>setEditForm({...editForm, department:e.target.value})} placeholder="(optional)"/>
            <p className="text-xs text-neutral-500 pt-1">Token will <strong>not</strong> change.{editing?.token ? <> Current: <code className="bg-neutral-200 px-1 break-all">{editing.token}</code></> : null}</p>
          </div>
          <DialogFooter>
            <button onClick={closeEdit} className="brutal-btn-secondary text-sm">Cancel</button>
            <button data-testid="edit-user-save-btn" onClick={saveEdit} disabled={editBusy} className="brutal-btn text-sm">{editBusy?"Saving…":"Save changes"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Points Log Dialog */}
      <Dialog open={!!plUser} onOpenChange={(o) => { if (!o) { setPlUser(null); setPlData(null); } }}>
        <DialogContent className="border-4 border-black rounded-none brutal-shadow max-w-lg">
          <DialogHeader>
            <DialogTitle className="uppercase font-black flex items-center gap-2"><Trophy size={18}/> Points Log — {plUser?.name}</DialogTitle>
          </DialogHeader>
          {!plData ? (
            <div className="p-6 text-center text-sm text-neutral-500">Loading…</div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between border-2 border-black p-3 bg-[#FFFCE5]">
                <span className="font-black uppercase text-sm">Total Points</span>
                <span className="font-black text-2xl" data-testid="admin-pl-total">{plData.total}</span>
              </div>
              <div className="max-h-80 overflow-y-auto border-2 border-black" data-testid="admin-points-log-list">
                {plData.items.length === 0 ? (
                  <div className="p-6 text-center text-sm text-neutral-500">No points yet.</div>
                ) : plData.items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-3 py-2 border-b border-neutral-200 text-sm">
                    <div className="min-w-0">
                      <div className="font-bold">{it.game} <span className="font-normal text-neutral-500">— {it.reason}</span></div>
                      <div className="text-xs text-neutral-400 font-mono">{it.date}</div>
                    </div>
                    <div className="font-black text-[#16a34a] whitespace-nowrap">+{it.points}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default UsersPanel;
